'use client';

import { useEffect } from 'react';

import { useRouter } from '~/i18n/routing';

// Matches the `x-nextjs-stale-time: 300` the pages send, Next's default
// `staleTimes.static` (next.config.ts sets none); a quick tab switch costs no
// request.
const AWAY_MS = 5 * 60 * 1000;

/**
 * A page left open in a tab, or restored from the browser's back/forward
 * cache, asks the server nothing, so a price changed meanwhile never reaches
 * it. On return after more than five minutes away this refreshes the route's
 * server data in place: `router.refresh()` keeps client state and scroll.
 *
 * Time away is read from the wall clock, never a timer: a locked phone
 * suspends timers. A return can fire both `visibilitychange` and a persisted
 * `pageshow`; the first one clears the departure, so it refreshes once.
 */
export const RefreshOnReturn = () => {
  const router = useRouter();

  useEffect(() => {
    // A tab opened in the background mounts hidden: its data dates from now.
    let hiddenAt: number | null = document.visibilityState === 'hidden' ? Date.now() : null;

    const onReturn = () => {
      if (hiddenAt !== null && Date.now() - hiddenAt > AWAY_MS) {
        router.refresh();
      }

      hiddenAt = null;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt ??= Date.now();
      } else {
        onReturn();
      }
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        onReturn();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [router]);

  return null;
};
